import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { timeAgo } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PhoneCall, MessageCircle, MessageSquare, Flame } from "lucide-react";

const CH_ICON = { call: PhoneCall, whatsapp: MessageCircle, sms: MessageSquare };
const STATUS = {
  open: { label: "Open", cls: "border-blue-400/50 text-blue-300 bg-blue-500/10" },
  pending: { label: "Pending", cls: "border-amber-400/50 text-amber-300 bg-amber-500/10" },
  resolved: { label: "Resolved", cls: "border-emerald-400/50 text-emerald-300 bg-emerald-500/10" },
};

export default function Tickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [status, setStatus] = useState("all");

  const load = useCallback(async () => {
    const { data } = await api.get(`/tickets?status=${status}`);
    setTickets(data);
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const counts = { all: tickets.length };

  return (
    <div>
      <PageHeader eyebrow="Ticketing" title="Support Tickets"
        subtitle="Assign, track and resolve every pilgrim enquiry"
        action={
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="ticket-filter-status" className="w-40 bg-card/60 border-gold/30"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
        }
      />
      <div className="p-4 sm:p-8 space-y-3">
        {tickets.length === 0 && <p className="text-sm text-muted-foreground text-center py-12">No tickets found.</p>}
        {tickets.map((t) => {
          const Icon = CH_ICON[t.channel] || MessageCircle;
          return (
            <Card key={t.id} data-testid={`ticket-row-${t.id}`} onClick={() => navigate(`/customers/${t.contact_id}`)}
              className="p-4 bg-card/40 border-border/60 card-hover cursor-pointer flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/40 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4 text-gold" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium truncate">{t.subject}</p>
                  {t.priority === "high" && <Flame className="w-3.5 h-3.5 text-destructive" />}
                </div>
                <p className="text-xs text-muted-foreground truncate">{t.contact?.name} · {t.contact?.phone}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <Badge variant="outline" className={STATUS[t.status]?.cls}>{STATUS[t.status]?.label}</Badge>
                <p className="text-[10px] text-muted-foreground mt-1">{timeAgo(t.updated_at)}</p>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
